import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import SearchableSelect from '../components/SearchableSelect';
import { listOptions, useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/ReceiptForm.jsx.
//
// Renamed vs. source (see ReceiptController::store() on the backend):
// `donor_id` -> `member_id`, `/donors` -> `/members` — same reasoning as
// CustomerInvoiceForm.tsx. The Credit Sale tab posts to
// `/customer-invoices/{id}/receipts` (CustomerInvoiceController::storeReceipt,
// which only accepts paid_amount/payment_method/payment_date — no type/
// category/payer_name), the Cash Sale tab posts to `/receipts`
// (ReceiptController::store, which accepts the fuller field set including
// the optional Inventory sale path).
const PAYMENT_METHODS = [
  { value: 'paynow', label: 'PayNow' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

// Dropped vs. source: the source branched the payer-name label (and a
// category list, and whether the Member picker showed at all) on
// type === 'donation'/'zakat' — both mosque-specific concepts with no VNPF
// equivalent, and ReceiptController no longer gives `category` any special
// per-type validation. Category is a free-text field and the Member picker
// is always available, regardless of receipt type.
const NAME_LABELS: Record<string, string> = {
  general: 'Payer name',
  inventory: 'Buyer name',
};

interface CashFormState {
  type: string;
  category: string;
  member_id: string | number;
  inventory_item_id: string | number;
  quantity: string;
  payer_name: string;
  description: string;
  paid_amount: string;
  payment_method: string;
  payment_date: string;
  currency_code: string;
}

const emptyCashForm: CashFormState = {
  type: '',
  category: '',
  member_id: '',
  inventory_item_id: '',
  quantity: '',
  payer_name: '',
  description: '',
  paid_amount: '',
  payment_method: 'paynow',
  payment_date: new Date().toISOString().slice(0, 10),
  currency_code: 'VUV',
};

interface InvoiceOption {
  id: number;
  invoice_number: string;
  customer_name: string;
  amount: number;
  paid_total: number | null;
}

interface MemberOption {
  id: number;
  user: { first_name: string; last_name: string } | null;
}

interface InventoryItemOption {
  id: number;
  name: string;
  current_quantity: number;
  unit: string | null;
  unit_price: number | null;
  [key: string]: unknown;
}

type FormErrors = Record<string, string[]>;

export function ReceiptForm() {
  const { lists, settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedInvoiceId = searchParams.get('invoiceId') ?? '';
  const [mode, setMode] = useState<'cash' | 'credit'>(preselectedInvoiceId ? 'credit' : 'cash');
  const [errors, setErrors] = useState<FormErrors>({});

  // Credit Sale state
  const [invoices, setInvoices] = useState<InvoiceOption[] | null>(null);
  const [invoiceId, setInvoiceId] = useState(preselectedInvoiceId);
  const [creditForm, setCreditForm] = useState({
    paid_amount: '',
    payment_method: 'paynow',
    payment_date: new Date().toISOString().slice(0, 10),
  });

  // Cash Sale state
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [cashForm, setCashForm] = useState<CashFormState>(emptyCashForm);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<InventoryItemOption | null>(null);
  const [savingCash, setSavingCash] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>([]);

  useEffect(() => {
    api
      .get<{ data: InvoiceOption[] }>('/customer-invoices', { params: { per_page: 100 } })
      .then((res) => setInvoices(res.data.data.filter((inv) => Number(inv.paid_total ?? 0) < Number(inv.amount))));
    api
      .get<{ data: MemberOption[] }>('/members', { params: { per_page: 100 } })
      .then((res) => setMembers(res.data.data))
      .catch(() => setMembers([]));
    api
      .get<{ currency_code: string }[]>('/exchange-rates')
      .then((res) => setCurrencies(res.data.map((r) => r.currency_code)));
  }, []);

  const setCredit =
    (key: keyof typeof creditForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setCreditForm({ ...creditForm, [key]: e.target.value });
  const setCash =
    (key: keyof CashFormState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setCashForm({ ...cashForm, [key]: e.target.value });
  const setCashType = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedInventoryItem(null);
    setCashForm({
      ...cashForm,
      type: e.target.value,
      category: '',
      member_id: '',
      inventory_item_id: '',
      quantity: '',
    });
  };

  const setInventoryItem = (id: string | number, item?: InventoryItemOption) => {
    setSelectedInventoryItem(item ?? null);
    const quantity = cashForm.quantity || '1';
    setCashForm({
      ...cashForm,
      inventory_item_id: id,
      quantity: cashForm.quantity || (id ? quantity : ''),
      paid_amount:
        id && item?.unit_price != null ? String(Number(item.unit_price) * Number(quantity)) : cashForm.paid_amount,
    });
  };

  const setInventoryQuantity = (e: ChangeEvent<HTMLInputElement>) => {
    const quantity = e.target.value;
    setCashForm({
      ...cashForm,
      quantity,
      paid_amount:
        selectedInventoryItem?.unit_price != null && quantity
          ? String(Number(selectedInventoryItem.unit_price) * Number(quantity))
          : cashForm.paid_amount,
    });
  };

  const selectedInvoice = invoices?.find((inv) => String(inv.id) === invoiceId);
  const balance = selectedInvoice ? Number(selectedInvoice.amount) - Number(selectedInvoice.paid_total ?? 0) : 0;

  const saveCredit = async (e: FormEvent) => {
    e.preventDefault();
    if (savingCredit) return;
    if (!invoiceId) {
      setErrors({ general: ['Select an invoice to record a payment against.'] });
      return;
    }
    setSavingCredit(true);
    try {
      await api.post(`/customer-invoices/${invoiceId}/receipts`, {
        ...creditForm,
        paid_amount: creditForm.paid_amount || balance,
      });
      navigate('/revenue/receipts');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSavingCredit(false);
    }
  };

  const saveCash = async (e: FormEvent) => {
    e.preventDefault();
    if (savingCash) return;
    setSavingCash(true);
    try {
      await api.post('/receipts', {
        ...cashForm,
        category: cashForm.category || null,
        member_id: cashForm.member_id || null,
        inventory_item_id: cashForm.inventory_item_id || null,
        quantity: cashForm.quantity || null,
      });
      navigate('/revenue/receipts');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSavingCash(false);
    }
  };

  const cashTypeOptions = listOptions(lists, 'invoice_type')
    .filter((item) => item.value !== 'voucher')
    .map((item) => (item.label === 'Others' ? { ...item, label: 'Other' } : item));

  return (
    <>
      <header className="page-header">
        <h1>Add receipt</h1>
      </header>
      <p className="muted">
        {mode === 'cash'
          ? 'Record an immediate payment with no invoice — this issues a receipt straight away.'
          : 'Record a payment against an invoice — this issues a receipt automatically.'}
      </p>

      <div className="toolbar">
        <div className="tab-group">
          <button type="button" className={mode === 'cash' ? 'tab active' : 'tab'} onClick={() => setMode('cash')}>
            Cash Sale
          </button>
          <button type="button" className={mode === 'credit' ? 'tab active' : 'tab'} onClick={() => setMode('credit')}>
            Credit Sale
          </button>
        </div>
      </div>

      <div className="card">
        {mode === 'cash' ? (
          <form onSubmit={saveCash} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Type *
              <select value={cashForm.type} onChange={setCashType} required>
                <option value="">Select…</option>
                {cashTypeOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.type && <small className="error">{errors.type[0]}</small>}
            </label>
            <label>
              Category (optional)
              <input value={cashForm.category} onChange={setCash('category')} />
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label className="span-2">
              Member (optional)
              <select value={cashForm.member_id} onChange={setCash('member_id')}>
                <option value="">— Not a member —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.user ? `${m.user.first_name} ${m.user.last_name}` : `Member #${m.id}`}
                  </option>
                ))}
              </select>
            </label>
            {cashForm.type === 'inventory' && (
              <>
                <label className="span-2">
                  Item *
                  <SearchableSelect<InventoryItemOption>
                    endpoint="/inventory-items"
                    value={cashForm.inventory_item_id}
                    onChange={(id, item) => setInventoryItem(id, item)}
                    getLabel={(i) => i.name}
                    getSubLabel={(i) =>
                      `${Number(i.current_quantity)} ${i.unit ?? ''} in stock${i.unit_price != null ? ` · ${fmtMoney(i.unit_price)} each` : ''}`
                    }
                    placeholder="Search inventory items…"
                  />
                  {errors.inventory_item_id && <small className="error">{errors.inventory_item_id[0]}</small>}
                </label>
                <label>
                  Quantity *
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedInventoryItem ? Number(selectedInventoryItem.current_quantity) : undefined}
                    value={cashForm.quantity}
                    onChange={setInventoryQuantity}
                    required
                  />
                  {errors.quantity && <small className="error">{errors.quantity[0]}</small>}
                  {selectedInventoryItem && (
                    <small className="muted">{Number(selectedInventoryItem.current_quantity)} available</small>
                  )}
                </label>
              </>
            )}
            <label className="span-2">
              {NAME_LABELS[cashForm.type] ?? 'Payer name'} *
              <input value={cashForm.payer_name} onChange={setCash('payer_name')} required />
              {errors.payer_name && <small className="error">{errors.payer_name[0]}</small>}
            </label>
            <label>
              Amount *
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={cashForm.paid_amount}
                onChange={setCash('paid_amount')}
                required
              />
              {errors.paid_amount && <small className="error">{errors.paid_amount[0]}</small>}
            </label>
            <label>
              Currency
              <select value={cashForm.currency_code} onChange={setCash('currency_code')}>
                <option value="VUV">VUV</option>
                {currencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              {errors.currency_code && <small className="error">{errors.currency_code[0]}</small>}
            </label>
            <label>
              Payment method
              <select value={cashForm.payment_method} onChange={setCash('payment_method')}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment date
              <input type="date" value={cashForm.payment_date} onChange={setCash('payment_date')} />
            </label>
            <label className="span-2">
              Description
              <textarea rows={2} value={cashForm.description} onChange={setCash('description')} />
            </label>
            <div className="form-actions span-2">
              <Link className="btn btn-ghost" to="/revenue/receipts">
                Cancel
              </Link>
              <button className="btn btn-primary" disabled={savingCash}>
                {savingCash ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={saveCredit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Invoice *
              <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} required>
                <option value="">{invoices === null ? 'Loading…' : 'Select an invoice…'}</option>
                {invoices?.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} — {inv.customer_name} (Balance{' '}
                    {fmtMoney(Number(inv.amount) - Number(inv.paid_total ?? 0))})
                  </option>
                ))}
              </select>
              {invoices?.length === 0 && <small className="muted">No invoices with an outstanding balance.</small>}
            </label>
            <label>
              Amount ({settings.currency})
              <input
                type="number"
                step="0.01"
                value={creditForm.paid_amount}
                onChange={setCredit('paid_amount')}
                placeholder={selectedInvoice ? String(balance) : ''}
              />
              {errors.paid_amount && <small className="error">{errors.paid_amount[0]}</small>}
            </label>
            <label>
              Payment method
              <select value={creditForm.payment_method} onChange={setCredit('payment_method')}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Payment date
              <input type="date" value={creditForm.payment_date} onChange={setCredit('payment_date')} />
            </label>
            <div className="form-actions span-2">
              <Link className="btn btn-ghost" to="/revenue/receipts">
                Cancel
              </Link>
              <button className="btn btn-primary" disabled={savingCredit}>
                {savingCredit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
